import { z } from "zod";
import { conversationExecutionSchema } from "./conversations.js";
import { runtimeContinuityBindingSchema } from "./continuity.js";
import { AGENT_PROTOCOL_VERSION, appIdSchema, correlationIdSchema, eventIdSchema, isoTimestampSchema, opaqueRefSchema, runIdSchema, threadIdSchema, userIdSchema, } from "./identifiers.js";
import { agentRunPhaseSchema } from "./runs.js";
import { agentUsageSchema } from "./usage.js";
import { agentCardDeltaSchema, agentCardSchema } from "./cards.js";
import { agentMessagePresentationSchema } from "./messages.js";
import { runtimeTraceConfigSchema } from "./traces.js";
import { agentTaskListSchema } from "./task-lists.js";
import { runtimeTaskPreparationSchema } from "./task-preparation.js";
import { agentSkillCatalogIdentity, agentSkillCatalogSnapshotSchema, } from "./skills.js";
export const runtimeReasoningEffortSchema = z.enum([
    "none",
    "low",
    "medium",
    "high",
    "xhigh",
    "max",
]);
export const harnessVersionSchema = z
    .string()
    .trim()
    .min(1)
    .max(100)
    .regex(/^[a-z0-9][a-z0-9._-]+$/);
export const runtimeAuthorityModeSchema = z.enum([
    "legacy_split_v1",
    "conversation_only_v1",
    "app_owner_v1",
]);
export const APP_OWNER_MUTATION_JOURNAL_DIR = "/workspace/.opencloud/agent-mutations";
export const agentAppMutationJournalStateSchema = z.enum([
    "initializing",
    "ready",
    "retiring",
    "retired",
]);
export const agentAppMutationJournalGenerationSchema = z.uuid();
const runtimeMutationJournalStateSchema = z.enum(["initializing", "ready"]);
export const appOwnerCredentialRefSchema = z
    .string()
    .trim()
    .min(1)
    .max(100)
    .superRefine((value, context) => {
    const match = /^app-owner-credential:([^:]+):([1-9][0-9]*)$/.exec(value);
    if (!match ||
        !z.uuid().safeParse(match[1]).success ||
        !Number.isSafeInteger(Number(match[2]))) {
        context.addIssue({
            code: "custom",
            message: "An app-owner credential reference must be app-owner-credential:<credentialId>:<generation>",
        });
    }
});
export function appOwnerCredentialRef(credentialId, generation) {
    const parsedCredentialId = z.uuid().parse(credentialId);
    const parsedGeneration = z.number().int().positive().parse(generation);
    return appOwnerCredentialRefSchema.parse(`app-owner-credential:${parsedCredentialId}:${parsedGeneration}`);
}
export const runtimeTaskCaptureSchema = z.object({
    state: z.literal("captured"), url: z.url().max(2_048), finalUrl: z.url().max(2_048),
    capturedAt: isoTimestampSchema, sha256: z.string().regex(/^[a-f0-9]{64}$/),
    byteSize: z.number().int().min(8).max(10 * 1024 * 1024), width: z.literal(1440), height: z.literal(1000),
    imagePath: z.string().regex(/^\/workspace\/task-captures-[a-zA-Z0-9-]+\/[a-f0-9]{64}\.png$/).optional(),
}).strict();
export const runtimeTaskRecoverySchema = z.array(z.object({
    assignmentId: z.string().min(1).max(128), completed: z.boolean(),
    operationId: z.uuid().nullable(), screenshotFileId: z.uuid().nullable(),
    capture: runtimeTaskCaptureSchema.nullable(),
}).strict()).max(50);
export const runtimeInstallationSchema = z
    .object({
    skillUrl: z.url().max(2_048),
    brandPromptFragment: z.string().trim().min(1).max(2_000).optional(),
})
    .strict();
export const runtimeStartSchema = z
    .object({
    continuity: runtimeContinuityBindingSchema.optional(),
    workspaceGeneration: z.uuid().optional(),
    workspaceChange: z.object({
        previousVersion: z.number().int().positive(),
        version: z.number().int().positive(),
    }).strict().refine(value => value.version > value.previousVersion, "Workspace change must advance its revision").optional(),
    conversationExecution: conversationExecutionSchema.optional(),
    protocolVersion: z.literal(AGENT_PROTOCOL_VERSION),
    runId: runIdSchema,
    parentRunId: runIdSchema.nullable(),
    kind: z.enum(["root", "child"]),
    userId: userIdSchema,
    appId: appIdSchema,
    threadId: threadIdSchema,
    correlationId: correlationIdSchema,
    eventSequence: z.number().int().nonnegative(),
    trace: runtimeTraceConfigSchema,
    workspaceRef: opaqueRefSchema,
    taskRecovery: runtimeTaskRecoverySchema.optional(),
    taskPreparation: runtimeTaskPreparationSchema.optional(),
    checkpoint: z
        .object({
        version: z.number().int().nonnegative(),
        ref: opaqueRefSchema,
        digest: z.string().regex(/^sha256:[a-f0-9]{64}$/),
    })
        .strict()
        .nullable(),
    resume: z
        .object({
        mode: z.literal("hot_workspace"),
    })
        .strict()
        .optional(),
    engine: z
        .object({
        adapter: z.string().trim().min(1).max(100),
        model: z.string().trim().min(1).max(200).nullable(),
        reasoningEffort: runtimeReasoningEffortSchema.optional(),
        harnessVersion: harnessVersionSchema.optional(),
    })
        .strict(),
    providerConnectionRef: opaqueRefSchema,
    authorityMode: runtimeAuthorityModeSchema.default("legacy_split_v1"),
    appActivationId: z.uuid().optional(),
    authorityGrantRef: opaqueRefSchema.optional(),
    // The model-readable development grant is distinct from the trusted root
    // grant referenced above. Legacy and non-AI-SDK runtimes omit it.
    modelAuthorityGrantRef: opaqueRefSchema.optional(),
    appOwnerCredentialRef: appOwnerCredentialRefSchema.optional(),
    mutationJournalDir: z.literal(APP_OWNER_MUTATION_JOURNAL_DIR).optional(),
    mutationJournalGeneration: agentAppMutationJournalGenerationSchema.optional(),
    mutationJournalState: runtimeMutationJournalStateSchema.optional(),
    callback: z
        .object({
        url: z.url(),
        tokenRef: opaqueRefSchema,
        providerStateUrl: z.url().optional(),
        providerAccessUrl: z.url().optional(),
        attachmentsUrl: z.url().optional(),
        continuityUrl: z.url().max(2_048).optional(),
        workspaceUrl: z.url().max(2_048).optional(),
    })
        .strict(),
    resources: z
        .object({
        memoryBytes: z
            .number()
            .int()
            .min(256 * 1024 * 1024),
        nanoCpus: z.number().int().min(100_000_000),
        pidsLimit: z.number().int().min(32),
        maxChildAgents: z.number().int().min(0).max(32),
    })
        .strict(),
    // AI-SDK Codex roots and relaunches carry their immutable root snapshot.
    // Historical runs and harnesses that do not consume catalog skills omit it.
    skillCatalog: agentSkillCatalogSnapshotSchema.optional(),
    // The installation's public builder skill and, for a brand that names
    // itself to people, Agent guidance from its brand pack. Harnesses bind
    // the fragment into their rendered digest only when it is present.
    installation: runtimeInstallationSchema.optional(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.continuity && value.workspaceGeneration !== value.continuity.workspaceGeneration) {
        context.addIssue({ code: "custom", path: ["workspaceGeneration"], message: "Continuity requires its registered workspace generation" });
    }
    if (value.callback.workspaceUrl && (!value.workspaceGeneration || value.continuity || value.kind !== "root")) {
        context.addIssue({ code: "custom", path: ["callback", "workspaceUrl"], message: "Source checkpoints require a registered non-conversation root" });
    }
    if (value.workspaceChange && !value.continuity) {
        context.addIssue({ code: "custom", path: ["workspaceChange"], message: "Workspace reconciliation requires a registered conversation" });
    }
    if (Boolean(value.continuity) !== Boolean(value.callback.continuityUrl) ||
        (value.continuity && value.kind !== "root")) {
        context.addIssue({ code: "custom", path: ["continuity"], message: "Root continuity and its callback URL must be present together" });
    }
    if (value.kind === "root" &&
        value.workspaceRef !== value.trace.manifest.runtime.workspaceRef) {
        context.addIssue({
            code: "custom",
            path: ["workspaceRef"],
            message: "Runtime workspace identity must match the immutable trace manifest",
        });
    }
    const usesAppOwnerAuthority = value.authorityMode === "app_owner_v1";
    const usesConversationOnlyAuthority = value.authorityMode === "conversation_only_v1";
    const usesV3Authority = usesAppOwnerAuthority || usesConversationOnlyAuthority;
    const usesV3Harness = value.kind === "root" &&
        ((value.engine.adapter === "codex" &&
            (value.engine.harnessVersion === "opencloud-codex-ai-sdk-harness-v3" ||
                value.engine.harnessVersion === "opencloud-opencode2-harness-v1" ||
                // The Codex engine with an OpenRouter provider block (decision 0031).
                value.engine.harnessVersion === "opencloud-codex-openrouter-harness-v1" ||
                // OpenCode 2 on OpenRouter API keys, in the Codex adapter slot.
                value.engine.harnessVersion === "opencloud-opencode2-openrouter-harness-v1")) ||
            (value.engine.adapter === "claude" &&
                (value.engine.harnessVersion === "opencloud-claude-code-harness-v1" ||
                    value.engine.harnessVersion === "opencloud-opencode2-anthropic-harness-v1")));
    if (usesV3Authority !== usesV3Harness) {
        context.addIssue({
            code: "custom",
            path: ["authorityMode"],
            message: "An app-owner harness root runtime requires conversation-only or app-owner authority",
        });
    }
    if (usesAppOwnerAuthority &&
        (value.authorityGrantRef !== undefined ||
            value.modelAuthorityGrantRef !== undefined)) {
        context.addIssue({
            code: "custom",
            path: ["authorityGrantRef"],
            message: "App-owner authority cannot include legacy grant references",
        });
    }
    if (value.authorityMode === "legacy_split_v1" &&
        (value.authorityGrantRef === undefined ||
            value.appOwnerCredentialRef !== undefined)) {
        context.addIssue({
            code: "custom",
            path: ["authorityGrantRef"],
            message: "Legacy authority requires one trusted grant and no app-owner credential reference",
        });
    }
    if (usesConversationOnlyAuthority &&
        (value.authorityGrantRef !== undefined ||
            value.modelAuthorityGrantRef !== undefined ||
            value.appOwnerCredentialRef !== undefined ||
            value.mutationJournalDir !== undefined ||
            value.mutationJournalGeneration !== undefined ||
            value.mutationJournalState !== undefined)) {
        context.addIssue({
            code: "custom",
            path: ["authorityMode"],
            message: "Conversation-only authority cannot include application authority or mutation-journal references",
        });
    }
    const standalone = value.conversationExecution?.subject.kind === "standalone";
    if (standalone && (value.appId !== value.conversationExecution.partitionId ||
        !usesConversationOnlyAuthority ||
        value.conversationExecution.subject.conversationId !== value.threadId)) {
        context.addIssue({ code: "custom", path: ["conversationExecution"], message: "Standalone execution requires its isolated partition and conversation-only authority." });
    }
    if ((!standalone && Boolean(value.appActivationId) !== usesConversationOnlyAuthority) || (value.appActivationId && !usesConversationOnlyAuthority)) {
        context.addIssue({
            code: "custom",
            path: ["appActivationId"],
            message: "Conversation-only authority requires one exact app activation identity",
        });
    }
    if (Boolean(value.appOwnerCredentialRef) !== usesAppOwnerAuthority ||
        Boolean(value.mutationJournalDir) !== usesAppOwnerAuthority ||
        Boolean(value.mutationJournalGeneration) !== usesAppOwnerAuthority ||
        Boolean(value.mutationJournalState) !== usesAppOwnerAuthority) {
        context.addIssue({
            code: "custom",
            path: ["appOwnerCredentialRef"],
            message: "App-owner authority requires its credential reference and exact mutation-journal generation",
        });
    }
    const usesSplitAuthority = value.authorityMode === "legacy_split_v1" &&
        value.kind === "root" &&
        value.engine.adapter === "codex" &&
        [
            "opencloud-codex-ai-sdk-harness-v1",
            "opencloud-codex-ai-sdk-harness-v2",
        ].includes(value.engine.harnessVersion ?? "");
    if (Boolean(value.modelAuthorityGrantRef) !== usesSplitAuthority) {
        context.addIssue({
            code: "custom",
            path: ["modelAuthorityGrantRef"],
            message: "Only an AI-SDK Codex root runtime requires model authority",
        });
    }
    if (value.kind === "root" && value.parentRunId !== null) {
        context.addIssue({
            code: "custom",
            path: ["parentRunId"],
            message: "A root run cannot have a parentRunId",
        });
    }
    if (value.kind === "child" && value.parentRunId === null) {
        context.addIssue({
            code: "custom",
            path: ["parentRunId"],
            message: "A child run requires a parentRunId",
        });
    }
    if (value.resume?.mode === "hot_workspace" && value.kind !== "root") {
        context.addIssue({
            code: "custom",
            path: ["resume"],
            message: "Only a root run can resume from its hot workspace",
        });
    }
    const expectedTraceRoot = value.kind === "root" ? value.runId : value.parentRunId;
    if (value.trace.rootRunId !== expectedTraceRoot) {
        context.addIssue({
            code: "custom",
            path: ["trace", "rootRunId"],
            message: "Runtime trace root does not match run lineage",
        });
    }
    if (value.trace.manifest.userId !== value.userId ||
        value.trace.manifest.appId !== value.appId ||
        value.trace.manifest.threadId !== value.threadId ||
        value.trace.manifest.correlationId !== value.correlationId) {
        context.addIssue({
            code: "custom",
            path: ["trace", "manifest"],
            message: "Runtime trace manifest does not match the runtime identity",
        });
    }
    const traceCatalog = value.trace.manifest.skillCatalog;
    if (value.kind === "root" &&
        !usesConversationOnlyAuthority &&
        Boolean(value.skillCatalog) !== Boolean(traceCatalog)) {
        context.addIssue({
            code: "custom",
            path: ["skillCatalog"],
            message: "Runtime and trace skill snapshots must be present together",
        });
    }
    if (value.skillCatalog &&
        JSON.stringify(agentSkillCatalogIdentity(value.skillCatalog)) !==
            JSON.stringify(traceCatalog)) {
        context.addIssue({
            code: "custom",
            path: ["skillCatalog"],
            message: "Runtime skill snapshot differs from its trace identity",
        });
    }
    const commandCatalog = value.trace.manifest.initialCommand.type === "run.start" ||
        value.trace.manifest.initialCommand.type === "run.continue"
        ? value.trace.manifest.initialCommand.skillCatalog
        : undefined;
    if (usesConversationOnlyAuthority &&
        (Boolean(commandCatalog) !== Boolean(traceCatalog) ||
            (commandCatalog &&
                JSON.stringify(agentSkillCatalogIdentity(commandCatalog)) !==
                    JSON.stringify(traceCatalog)))) {
        context.addIssue({
            code: "custom",
            path: ["trace", "manifest", "skillCatalog"],
            message: "Conversation-only trace skill identity differs from its root command",
        });
    }
    if ((!usesConversationOnlyAuthority || value.skillCatalog !== undefined) &&
        commandCatalog &&
        JSON.stringify(commandCatalog) !== JSON.stringify(value.skillCatalog)) {
        context.addIssue({
            code: "custom",
            path: ["skillCatalog"],
            message: "Runtime skill snapshot differs from its root command",
        });
    }
});
const eventBase = {
    protocolVersion: z.literal(AGENT_PROTOCOL_VERSION),
    eventId: eventIdSchema,
    runId: runIdSchema,
    sequence: z.number().int().nonnegative(),
    correlationId: correlationIdSchema,
    occurredAt: isoTimestampSchema,
};
export const runtimeToolInvocationSchema = z
    .object({
    kind: z.enum(["command", "tool"]),
    summary: z.string().trim().min(1).max(1_000),
    input: z.string().max(32_000).nullable(),
})
    .strict();
export const runtimePublicCliReceiptSchema = z
    .object({
    status: z.enum(["succeeded", "failed", "cancelled", "pending"]),
    exitCode: z.number().int().min(0).max(255).nullable(),
    operationIds: z.array(z.uuid()).max(32),
    summary: z.string().trim().min(1).max(1_000),
})
    .strict();
export const runtimeToolResultSchema = z
    .object({
    output: z.string().max(64_000).nullable(),
    publicCli: runtimePublicCliReceiptSchema.optional(),
})
    .strict();
const sha256DigestSchema = z.string().regex(/^sha256:[a-f0-9]{64}$/);
const harnessRoleSchema = z.enum([
    "builder",
    "functional-tester",
    "product-reviewer",
    "opencloud-namer",
    "opencloud-worker",
]);
const verificationDispositionSchema = z.enum([
    "passed",
    "failed",
    "not_applicable",
    "not_tested",
]);
export const harnessMilestoneSchema = z.enum([
    "first_edit",
    "artifact_valid",
    "preview_ready",
    "dev_verified",
    "deployment_started",
    "production_verified",
]);
export const providerBlockerKindSchema = z.enum([
    "rate_limit",
    "usage_limit",
    "authentication",
    "provider_unavailable",
]);
export const providerRequiredActionSchema = z.enum([
    "wait",
    "reconnect_provider",
]);
const providerBlockerFields = {
    blockerKind: providerBlockerKindSchema,
    provider: z.string().trim().min(1).max(100),
    retryAt: isoTimestampSchema.nullable(),
    requiredAction: providerRequiredActionSchema,
};
const userInputOptionSchema = z
    .object({
    id: z
        .string()
        .trim()
        .min(1)
        .max(100)
        .regex(/^[a-z0-9][a-z0-9._-]*$/),
    label: z.string().trim().min(1).max(200),
    detail: z.string().trim().min(1).max(1_000),
    response: z.string().trim().min(1).max(5_000).optional(),
    tone: z.enum(["default", "primary", "destructive", "decline"]).optional(),
})
    .strict();
export const runtimeEventSchema = z.discriminatedUnion("type", [
    z
        .object({
        ...eventBase,
        type: z.literal("harness.started"),
        harnessVersion: harnessVersionSchema,
        harnessDigest: sha256DigestSchema,
    })
        .strict(),
    z
        .object({
        ...eventBase,
        type: z.literal("runtime.ready"),
        runtimeId: opaqueRefSchema,
        acceptsLiveUserMessages: z.boolean().optional(),
        acceptsWarmUserMessages: z.boolean().optional(),
    })
        .strict(),
    z
        .object({
        ...eventBase,
        type: z.literal("command.native.accepted"),
        messageId: z.uuid(),
        commandType: z.enum(["run.continue", "user.answer"]),
        contentSha256: sha256DigestSchema,
        nativeTurnId: opaqueRefSchema,
        source: z.enum([
            "turn_start",
            "turn_steer",
            "server_request_resolved",
            "turn_completed_after_answer",
        ]),
        nativeRequestId: opaqueRefSchema.nullable(),
    })
        .strict()
        .superRefine((value, context) => {
        if (value.commandType === "run.continue" &&
            (!["turn_start", "turn_steer"].includes(value.source) || value.nativeRequestId !== null)) {
            context.addIssue({
                code: "custom",
                message: "A run.continue acceptance requires turn_start or turn_steer and no native request",
            });
        }
        if (value.commandType === "user.answer" &&
            (["turn_start", "turn_steer"].includes(value.source) || value.nativeRequestId === null)) {
            context.addIssue({
                code: "custom",
                message: "A user.answer acceptance requires a resolved native request identity",
            });
        }
    }),
    z
        .object({
        ...eventBase,
        type: z.literal("harness.milestone"),
        milestone: harnessMilestoneSchema,
        source: z.literal("trusted-observer"),
    })
        .strict(),
    z
        .object({
        ...eventBase,
        type: z.literal("phase.changed"),
        phase: agentRunPhaseSchema,
    })
        .strict(),
    z
        .object({
        ...eventBase,
        type: z.literal("message.delta"),
        messageId: z.uuid(),
        delta: z.string().min(1).max(100_000),
    })
        .strict(),
    z
        .object({
        ...eventBase,
        type: z.literal("message.completed"),
        messageId: z.uuid(),
        content: z.string().min(1).max(200_000),
        presentation: agentMessagePresentationSchema.optional(),
    })
        .strict(),
    z
        .object({
        ...eventBase,
        type: z.literal("tool.started"),
        toolCallId: z.uuid(),
        tool: z.string().trim().min(1).max(200),
        invocation: runtimeToolInvocationSchema.optional(),
    })
        .strict(),
    z
        .object({
        ...eventBase,
        type: z.literal("tool.completed"),
        toolCallId: z.uuid(),
        tool: z.string().trim().min(1).max(200),
        outcome: z.enum(["succeeded", "failed", "cancelled"]),
        durationMs: z.number().int().nonnegative(),
        errorClass: z.string().trim().min(1).max(200).nullable(),
        invocation: runtimeToolInvocationSchema.optional(),
        result: runtimeToolResultSchema.optional(),
    })
        .strict(),
    z
        .object({
        ...eventBase,
        type: z.literal("app.activation.handoff"),
        activationId: z.uuid(),
        nativeTurnId: opaqueRefSchema,
        toolCallId: z.uuid(),
    })
        .strict(),
    z
        .object({
        ...eventBase,
        type: z.literal("card.created"),
        sourceToolCallId: z.string().trim().min(1).max(500),
        card: agentCardSchema,
    })
        .strict(),
    z
        .object({
        ...eventBase,
        type: z.literal("card.updated"),
        sourceToolCallId: z.string().trim().min(1).max(500),
        update: agentCardDeltaSchema,
    })
        .strict(),
    z
        .object({
        ...eventBase,
        type: z.literal("task_list.updated"),
        taskList: agentTaskListSchema,
    })
        .strict(),
    z.object({
        ...eventBase,
        type: z.literal("task.preparation"),
        preparation: runtimeTaskPreparationSchema,
    }).strict(),
    z.object({
        ...eventBase,
        type: z.literal("task.capture"),
        assignmentId: z.string().min(1).max(128),
        capture: z.discriminatedUnion("state", [
            runtimeTaskCaptureSchema,
            z.object({ state: z.literal("failed"), url: z.string().max(2_048),
                reason: z.enum(["blocked", "capture_failed"]),
            }).strict(),
        ]),
    }).strict(),
    z
        .object({
        ...eventBase,
        type: z.literal("user.input.required"),
        questionId: z.uuid(),
        question: z.string().trim().min(1).max(5_000),
        reason: z.enum(["material_decision", "new_authority", "policy"]),
        context: z.string().trim().min(1).max(5_000).optional(),
        options: z.array(userInputOptionSchema).min(2).max(5).optional(),
    })
        .strict(),
    z
        .object({
        ...eventBase,
        type: z.literal("checkpoint.committed"),
        checkpointVersion: z.number().int().nonnegative(),
        checkpointRef: opaqueRefSchema,
        digest: z.string().regex(/^sha256:[a-f0-9]{64}$/),
    })
        .strict(),
    z
        .object({
        ...eventBase,
        type: z.literal("usage.reported"),
        usage: agentUsageSchema,
    })
        .strict(),
    z
        .object({
        ...eventBase,
        type: z.literal("provider.retrying"),
        ...providerBlockerFields,
        attempt: z.number().int().positive(),
    })
        .strict(),
    z
        .object({
        ...eventBase,
        type: z.literal("provider.recovered"),
        provider: z.string().trim().min(1).max(100),
    })
        .strict(),
    z
        .object({
        ...eventBase,
        type: z.literal("run.blocked"),
        ...providerBlockerFields,
    })
        .strict(),
    z
        .object({
        ...eventBase,
        type: z.literal("harness.outcome"),
        harnessVersion: harnessVersionSchema,
        harnessDigest: sha256DigestSchema,
        status: z.enum(["passed", "failed"]),
        artifactDigest: sha256DigestSchema.nullable(),
        activeDeploymentId: z.uuid().nullable(),
        activeDeploymentArtifactDigest: sha256DigestSchema.nullable(),
        validationPassed: z.boolean(),
        newDeploymentActivated: z.boolean(),
        children: z
            .array(z
            .object({
            role: harnessRoleSchema,
            artifactDigest: sha256DigestSchema.nullable(),
            completed: z.boolean(),
            naturalLanguageHandoff: z.boolean(),
            deliveredToLead: z.boolean(),
            returnedBeforeCompletion: z.boolean(),
            resolution: z.enum([
                "accepted",
                "repair_required",
                "not_required",
            ]),
        })
            .strict())
            .max(3),
        progress: z
            .object({
            leadUpdates: z.number().int().nonnegative(),
            fallbackUpdates: z.number().int().nonnegative(),
            largestGapMs: z.number().int().nonnegative(),
            cadencePassed: z.boolean(),
        })
            .strict(),
        credentialScan: z
            .object({
            clean: z.boolean(),
            scannedFiles: z.number().int().nonnegative(),
        })
            .strict(),
        devVerification: z
            .object({
            receiptId: z.uuid().nullable(),
            artifactDigest: sha256DigestSchema.nullable(),
            passed: z.boolean(),
            browserContract: z.enum(["executed", "not_applicable", "not_tested"]),
            distinctSyntheticUsers: z.boolean(),
            phases: z
                .array(z
                .object({
                name: z.string().trim().min(1).max(100),
                status: verificationDispositionSchema,
            })
                .strict())
                .max(50),
        })
            .strict(),
        missing: z.array(z.string().trim().min(1).max(100)).max(30),
    })
        .strict()
        .superRefine((value, context) => {
        if (value.status !== "passed")
            return;
        const issue = (path, message) => {
            context.addIssue({ code: "custom", path, message });
        };
        if (value.missing.length > 0) {
            issue(["missing"], "A passed harness outcome cannot contain missing checks");
        }
        if (!value.artifactDigest ||
            !value.activeDeploymentId ||
            value.activeDeploymentArtifactDigest !== value.artifactDigest ||
            !value.validationPassed ||
            !value.newDeploymentActivated) {
            issue([], "A passed harness outcome requires exact validated deployment evidence");
        }
        const roles = new Map(value.children.map((child) => [child.role, child]));
        const singleAgentHarness = [
            "opencloud-product-harness-todo-v3",
            "opencloud-product-harness-todo-v4",
            "opencloud-product-harness-open-v2",
            "opencloud-product-harness-open-v3",
            "opencloud-product-harness-open-v4",
            "opencloud-product-harness-open-v5",
            "opencloud-product-harness-open-v6",
            "opencloud-product-harness-open-v7",
            "opencloud-codex-direct-v3",
            "opencloud-codex-direct-v4",
            "opencloud-codex-ai-sdk-harness-v1",
            "opencloud-codex-ai-sdk-harness-v2",
        ].includes(value.harnessVersion);
        if (singleAgentHarness) {
            if (value.children.length !== 0) {
                issue(["children"], "A passed primary-writer harness outcome cannot contain required child evidence");
            }
        }
        else {
            if (value.children.length !== 3 ||
                roles.size !== 3 ||
                !["builder", "functional-tester", "product-reviewer"].every((role) => roles.has(role))) {
                issue(["children"], "A passed harness outcome requires all three child roles");
            }
            for (const [role, child] of roles) {
                if (child.artifactDigest !== value.artifactDigest ||
                    !child.completed ||
                    !child.naturalLanguageHandoff ||
                    !child.deliveredToLead ||
                    !child.returnedBeforeCompletion ||
                    (role === "builder"
                        ? child.resolution !== "not_required"
                        : child.resolution !== "accepted")) {
                    issue(["children"], "Passed child evidence is incomplete or inconsistent");
                    break;
                }
            }
        }
        const trustedObserverProgress = [
            "opencloud-product-harness-v11",
            "opencloud-product-harness-converse-v1",
            "opencloud-product-harness-todo-v2",
            "opencloud-product-harness-todo-v3",
            "opencloud-product-harness-todo-v4",
            "opencloud-product-harness-open-v2",
            "opencloud-product-harness-open-v3",
            "opencloud-product-harness-open-v4",
            "opencloud-product-harness-open-v5",
            "opencloud-product-harness-open-v6",
            "opencloud-product-harness-open-v7",
            "opencloud-codex-direct-v3",
            "opencloud-codex-direct-v4",
            "opencloud-codex-ai-sdk-harness-v1",
            "opencloud-codex-ai-sdk-harness-v2",
        ].includes(value.harnessVersion);
        const requiresProgressCadence = ![
            "opencloud-product-harness-open-v3",
            "opencloud-product-harness-open-v4",
            "opencloud-product-harness-open-v5",
            "opencloud-product-harness-open-v6",
            "opencloud-product-harness-open-v7",
            "opencloud-codex-direct-v4",
            "opencloud-codex-ai-sdk-harness-v1",
            "opencloud-codex-ai-sdk-harness-v2",
        ].includes(value.harnessVersion);
        if (requiresProgressCadence &&
            (!value.progress.cadencePassed ||
                (trustedObserverProgress
                    ? value.progress.leadUpdates + value.progress.fallbackUpdates < 1
                    : value.progress.leadUpdates < 1))) {
            issue(["progress"], "A passed harness outcome requires lead progress cadence");
        }
        if (!value.credentialScan.clean ||
            value.credentialScan.scannedFiles < 3) {
            issue(["credentialScan"], "A passed harness outcome requires a clean authored workspace");
        }
        if (!value.devVerification.receiptId ||
            value.devVerification.artifactDigest !== value.artifactDigest ||
            !value.devVerification.passed ||
            value.devVerification.browserContract !== "executed" ||
            !value.devVerification.distinctSyntheticUsers ||
            value.devVerification.phases.length === 0 ||
            value.devVerification.phases.some(({ status }) => status !== "passed" && status !== "not_applicable")) {
            issue(["devVerification"], "A passed harness outcome requires exact applicable verification evidence");
        }
    }),
    z
        .object({
        ...eventBase,
        type: z.literal("child.started"),
        childRunId: runIdSchema,
        role: harnessRoleSchema.optional(),
        purpose: z.string().trim().min(1).max(500),
    })
        .strict(),
    z
        .object({
        ...eventBase,
        type: z.literal("child.completed"),
        childRunId: runIdSchema,
        role: harnessRoleSchema.optional(),
        outcome: z.enum(["succeeded", "failed", "cancelled"]),
    })
        .strict(),
    z.object({ ...eventBase, type: z.literal("runtime.heartbeat") }).strict(),
    z
        .object({
        ...eventBase,
        type: z.literal("run.completed"),
        summary: z.string().trim().min(1).max(5_000),
        usageComplete: z.boolean().optional(),
        usageAccounting: z.object({
            consumedTokens: z.number().int().nonnegative(),
            pending: z.boolean(),
            nativeTotals: z.array(z.object({
                childRunId: z.uuid().nullable(), totalTokens: z.number().int().nonnegative(),
                baselineTotalTokens: z.number().int().nonnegative().nullable(),
            }).strict()).max(101),
        }).strict().optional(),
        completionKind: z
            .enum(["app_work", "conversation", "warm_timeout"])
            .optional(),
        // A completed interactive turn may retain its runtime for another turn.
        // Older runtimes omit this and retain their terminal completion fence.
        keepWarm: z.literal(true).optional(),
    })
        .strict(),
    z
        .object({
        ...eventBase,
        type: z.literal("run.failed"),
        errorClass: z.string().trim().min(1).max(200),
        message: z.string().trim().min(1).max(2_000),
        retryable: z.boolean(),
    })
        .strict(),
    z
        .object({
        ...eventBase,
        type: z.literal("run.cancelled"),
        reason: z.string().trim().min(1).max(500),
    })
        .strict(),
]);
//# sourceMappingURL=runtime.js.map